// SettingsModal.tsx
"use client";

import { Button } from "@workspace/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@workspace/web-ui/components/dialog";
import { Label } from "@workspace/web-ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/web-ui/components/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@workspace/web-ui/components/tabs";
import { Input } from "@workspace/web-ui/components/input";
import { useState } from "react";
import { Checkbox } from "@workspace/web-ui/components/checkbox";
import {
  bookingRulesQueryOptions,
  scheduleQueryOptions,
  bookingRulesExtraQueryOptions,
} from "../api/settings/queries";
import { useQuery } from "@tanstack/react-query";
import { useCalendarSettings } from "../hooks/use-calendar-settings-mutation";

// Booking Rules Tab
const BookingRulesTab = ({
  leadTime,
  interval,
  onUpdate,
}: {
  leadTime: string;
  interval: string;
  onUpdate: (updates: { leadTime?: string; interval?: string }) => void;
}) => {
  const [customInterval, setCustomInterval] = useState(() => {
    return !["15", "20", "30"].includes(interval) ? interval : "45";
  });

  const handleCustomIntervalChange = (value: string) => {
    const numValue = value.replace(/[^0-9]/g, "");
    if (numValue === "" || (parseInt(numValue) > 0 && parseInt(numValue) <= 120)) {
      setCustomInterval(numValue);
      if (numValue) {
        onUpdate({ interval: numValue });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="lead-time">Booking Lead Time</Label>
        <p className="text-sm text-gray-500 mb-2">
          Minimum days in advance for booking
        </p>
        <Select value={leadTime} onValueChange={(value) => onUpdate({ leadTime: value })}>
          <SelectTrigger id="lead-time">
            <SelectValue placeholder="Select days" />
          </SelectTrigger>
          <SelectContent>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((day) => (
              <SelectItem key={day} value={day.toString()}>
                {day === 0 ? "Today" : `${day} ${day > 1 ? 'days' : 'day'}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Time Interval</Label>
        <p className="text-sm text-gray-500 mb-2">
          Duration for each appointment slot
        </p>
        <div className="grid grid-cols-4 gap-2">
          {["15", "20", "30", "custom"].map((val) => (
            <Button
              key={val}
              variant={
                val === interval ||
                  (val === "custom" && !["15", "20", "30"].includes(interval))
                  ? "default"
                  : "outline"
              }
              onClick={() => {
                if (val === "custom") {
                  const customValue = customInterval || "45";
                  setCustomInterval(customValue);
                  onUpdate({ interval: customValue });
                } else {
                  onUpdate({ interval: val });
                }
              }}
            >
              {val === "custom" ? "Custom" : `${val} mins`}
            </Button>
          ))}
        </div>

        {!["15", "20", "30"].includes(interval) && (
          <div className="mt-2 border w-full">
            <div className="flex items-center border w-full gap-2">
              <Input
                type="number"
                min="1"
                max="120"
                value={customInterval}
                onChange={(e) => handleCustomIntervalChange(e.target.value)}
                placeholder="Enter minutes"
                className="w-full border-none outline-none"
              />
            </div>
            {parseInt(customInterval) > 120 && (
              <p className="text-sm text-red-500 mt-1">
                Please enter a value between 1 and 120 minutes
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Availability Tab
const AvailabilityTab = ({
  availability,
  timezone,
  onAvailabilityUpdate,
  onTimezoneUpdate,
  formatTimeInput,
}: {
  availability: Record<string, { available: boolean; startTime: string; endTime: string }>;
  timezone: string;
  onAvailabilityUpdate: (day: string, field: 'available' | 'startTime' | 'endTime', value: string | boolean) => void;
  onTimezoneUpdate: (timezone: string) => void;
  formatTimeInput: (time: string) => string;
}) => {
  const [editingTimes, setEditingTimes] = useState<Record<string, { startTime: string; endTime: string }>>({});

  return (
    <div className="space-y-4 flex-1 overflow-y-auto pr-2 hide-scrollbar h-[350px]">
      <div>
        <Label>Weekly Availability</Label>
        <p className="text-sm text-gray-500 mb-4">Set your recurring schedule (24-hour format)</p>
      </div>

      <div>
        <Label htmlFor="timezone" className="text-lg font-medium">
          Timezone
        </Label>
        <Select value={timezone} onValueChange={onTimezoneUpdate}>
          <SelectTrigger id="timezone">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {Intl.supportedValuesOf("timeZone").map((tz) => (
              <SelectItem key={tz} value={tz}>
                {tz}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
          const dayAvailability = availability[day] || {
            available: false,
            startTime: "09:00",
            endTime: "17:00",
          };

          return (
            <div key={day} className="flex flex-col">
              <div className="flex items-center justify-between">
                <div className="w-32 font-medium capitalize">{day}</div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`${day}-available`}
                    checked={dayAvailability.available}
                    onCheckedChange={(checked) =>
                      onAvailabilityUpdate(day, "available", !!checked)
                    }
                  />
                  <Label htmlFor={`${day}-available`} className="mr-4">
                    Available
                  </Label>
                </div>
              </div>

              {dayAvailability.available && (
                <div className="flex items-center justify-between space-x-2 ml-2">
                  <div className="relative w-full">
                    <Input
                      type="text"
                      value={editingTimes[day]?.startTime ?? dayAvailability.startTime}
                      onChange={(e) => {
                        setEditingTimes((prev) => ({
                          ...prev,
                          [day]: {
                            startTime: e.target.value,
                            endTime: prev[day]?.endTime ?? dayAvailability.endTime,
                          },
                        }));
                      }}
                      onBlur={(e) => {
                        const formatted = formatTimeInput(e.target.value);
                        onAvailabilityUpdate(day, "startTime", formatted);
                        setEditingTimes((prev) => ({
                          ...prev,
                          [day]: {
                            startTime: formatted,
                            endTime: prev[day]?.endTime ?? dayAvailability.endTime,
                          },
                        }));
                      }}
                      className="pr-12"
                      placeholder="HH:MM"
                      pattern="[0-9]{1,2}:[0-9]{2}"
                    />
                  </div>

                  <span className="px-2">To</span>

                  <div className="relative w-full">
                    <Input
                      type="text"
                      value={editingTimes[day]?.endTime ?? dayAvailability.endTime}
                      onChange={(e) => {
                        setEditingTimes((prev) => ({
                          ...prev,
                          [day]: {
                            startTime: prev[day]?.startTime ?? dayAvailability.startTime,
                            endTime: e.target.value,
                          },
                        }));
                      }}
                      onBlur={(e) => {
                        const formatted = formatTimeInput(e.target.value);
                        onAvailabilityUpdate(day, "endTime", formatted);
                        setEditingTimes((prev) => ({
                          ...prev,
                          [day]: {
                            startTime: prev[day]?.startTime ?? dayAvailability.startTime,
                            endTime: formatted,
                          },
                        }));
                      }}
                      className="pr-12"
                      placeholder="HH:MM"
                      pattern="[0-9]{1,2}:[0-9]{2}"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Advanced Tab
const AdvancedTab = ({
  sendReminders,
  meetingPrice,
  onUpdate,
}: {
  sendReminders: boolean;
  meetingPrice: string;
  onUpdate: (updates: { sendReminders?: boolean; meetingPrice?: string }) => void;
}) => {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-lg font-medium">Additional Options</Label>
        <div className="space-y-4 mt-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-reminders"
              checked={sendReminders}
              onCheckedChange={(checked) => onUpdate({ sendReminders: !!checked })}
            />
            <Label htmlFor="send-reminders">
              Send automated reminders to patients
            </Label>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="meeting-price" className="text-lg font-medium">
              Meeting Price
            </Label>
            <Input
              id="meeting-price"
              type="number"
              placeholder="Enter price in USD"
              className="mt-2"
              min="0"
              step="0.01"
              value={meetingPrice}
              onChange={(e) => onUpdate({ meetingPrice: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Settings Modal
export default function SettingsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (val: boolean) => void;
}) {
  const [activeTab, setActiveTab] = useState("rules");

  // Fetch all data
  const { data: bookingRulesData } = useQuery({
    ...bookingRulesQueryOptions(),
    enabled: open,
  });

  const { data: scheduleData } = useQuery({
    ...scheduleQueryOptions(),
    enabled: open,
  });

  const { data: bookingRulesExtraData } = useQuery({
    ...bookingRulesExtraQueryOptions(),
    enabled: open,
  });

  // Use the consolidated hook
  const {
    settings,
    updateBookingRules,
    updateAvailability,
    updateTimezone,
    updateAdvanced,
    saveAllSettings,
    isLoading,
    formatTimeInput,
  } = useCalendarSettings({
    bookingRulesData,
    scheduleData,
    bookingRulesExtraData,
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl p-6 max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Scheduling Settings
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          defaultValue="rules"
          className="mb-4"
        >
          <TabsList className="grid grid-cols-3 w-full bg-gray-100">
            <TabsTrigger value="rules">Booking Rules</TabsTrigger>
            <TabsTrigger value="availability">Availability</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="rules">
            <BookingRulesTab
              leadTime={settings.leadTime}
              interval={settings.interval}
              onUpdate={updateBookingRules}
            />
          </TabsContent>

          <TabsContent value="availability">
            <AvailabilityTab
              availability={settings.availability}
              timezone={settings.timezone}
              onAvailabilityUpdate={updateAvailability}
              onTimezoneUpdate={updateTimezone}
              formatTimeInput={formatTimeInput}
            />
          </TabsContent>

          <TabsContent value="advanced">
            <AdvancedTab
              sendReminders={settings.sendReminders}
              meetingPrice={settings.meetingPrice}
              onUpdate={updateAdvanced}
            />
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={saveAllSettings} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}